import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import './RfiForm.css';
import { Col, ControlLabel, Form, FormGroup } from 'react-bootstrap';
import moment from 'moment';
import lockPng from 'images/lock.png';
import Dropdown from 'components/shared/dropdown/Dropdown';
import MultipleDropdown from 'components/shared/dropdown/MultipleDropdown';
import DatetimePicker from 'components/shared/datetime-picker/DatetimePicker';
import { todayValidation } from 'services/utils/date-util';
import { countdown } from 'services/utils/date-util';
import TextareaAutosize from 'react-autosize-textarea';
import { usersUtil, ROLES } from 'services/utils/users-util';
import { Enterprise } from 'domain/enterprise';
import { Project } from 'domain/project';
import { RfiStatusKeys, RfiStatusEnum } from 'domain/rfi-status-enum';
import { loadProjectProposalRequest } from 'components/admin/projects/details/project-proposal/store/actions';
import { loadDirectoryRequest } from 'components/admin/projects/details/project-directory/store/actions';
import {
  updateRfiFormValue,
  setRfiFormValues,
} from 'components/admin/projects/details/project-rfi-log/store/actions';
import { getProjectProposal } from 'components/admin/projects/details/project-proposal/store/selectors';
import { getDirectory } from 'components/admin/projects/details/project-directory/store/selectors';
import { getRfiForm } from 'components/admin/projects/details/project-rfi-log/store/selectors';

class RfiForm extends Component {
  static propTypes = {
    currentUser: PropTypes.shape({}).isRequired,
    contribType: PropTypes.string.isRequired,
    project: PropTypes.shape({}).isRequired,
    currentRfi: PropTypes.shape({}),
  };

  constructor(props) {
    super(props);

    if (props.currentRfi) {
      this.initFormDetails(props.currentRfi);
    }
  }

  componentDidMount() {
    this.loadDirectory();
    if (!Enterprise.subcontractor(this.props.project.contributor_type)) {
      this.loadProjectProposal();
    }
  }

  nextStatus = (rfi) => {
    const { currentUser, project } = this.props;
    const assigned = !rfi.rfi_questions.find((qus) => !qus.assignee);
    const subcontractor =
      Enterprise.subcontractor(project.contributor_type) ||
      usersUtil.hasRole(currentUser, ROLES.GENERAL_CONTRACTOR_SUPERINTENDENT);
    return assigned && !subcontractor
      ? RfiStatusKeys.ASSIGNED
      : RfiStatusKeys.PENDING;
  };

  initStatus = (rfi) => {
    const stEnum = RfiStatusEnum.create(rfi.status);
    return RfiStatusEnum.DRAFT.equals(stEnum)
      ? this.nextStatus(rfi)
      : rfi.status;
  };

  initFormDetails = (rfi) => {
    this.props.dispatch(
      setRfiFormValues({
        ...rfi,
        csiCodeId: rfi.csi_code_id,
        contractorId: rfi.contractor_id,
        dueDate: rfi.due_date,
        extendedDueDate: rfi.extended_due_date,
        status: this.initStatus(rfi),
        relCsiCodes: rfi.rel_csi_codes.map((csiCode) => [
          csiCode.id,
          `${csiCode.division} - ${csiCode.label}`,
        ]),
        recipients: rfi.recipients.map((rcpt) => [
          rcpt.id,
          Project.userLabelStr(rcpt, rcpt.accountable),
        ]),
      })
    );
  };

  loadDirectory = () => {
    const { project, directory, dispatch } = this.props;
    if (!directory || directory.project_id !== project.id) {
      dispatch(loadDirectoryRequest(project.id));
    }
  };

  loadProjectProposal = () => {
    const { project, projectProposal, dispatch } = this.props;
    if (!projectProposal || projectProposal.project_id !== project.id) {
      dispatch(loadProjectProposalRequest(project.id));
    }
  };

  rfiCsiCodeOptions = () => {
    return Project.csiCodesOptions(this.props);
  };

  rfiCcCsiCodeOptions = () => {
    const { form } = this.props;
    return Project.csiCodesOptions(this.props)
      .filter((csiCode) => csiCode.value !== form.csiCodeId)
      .map((csiCode) => {
        return {
          id: csiCode.value,
          label: csiCode.label,
        };
      });
  };

  recipientOptions = () => {
    return Project.recipientOptions(this.props);
  };

  rfiSubcontractorOptions = (csiCodeId) => {
    return Project.subcontractorOptions(this.props, csiCodeId);
  };

  rfiStatusOptions = () => {
    return Object.keys(RfiStatusKeys)
      .filter((status) => !RfiStatusEnum.DRAFT.equals(RfiStatusEnum[status]))
      .map((status) => {
        const statusEnum = RfiStatusEnum[status];
        return {
          value: statusEnum.status,
          label: `${statusEnum.label
            .charAt(0)
            .toUpperCase()}${statusEnum.label.slice(1)}`,
        };
      });
  };

  handleFormChange = (attribute, value) => {
    this.props.dispatch(updateRfiFormValue(attribute, value));
  };

  handleCsiCodeChange = (csiCodeId) => {
    this.handleFormChange('csiCodeId', csiCodeId);
    this.handleFormChange('contractorId', null);

    const subOptions = this.rfiSubcontractorOptions(csiCodeId);
    if (subOptions.length === 1) {
      this.handleFormChange('contractorId', subOptions[0].value);
    }
  };

  render() {
    const { form, currentUser, contribType, currentRfi } = this.props;
    const subOptions = this.rfiSubcontractorOptions(form.csiCodeId);
    const statusLabel = form ? RfiStatusEnum.create(form.status).label : null;
    const subcontractor =
      Enterprise.subcontractor(contribType) ||
      usersUtil.hasRole(currentUser, ROLES.GENERAL_CONTRACTOR_SUPERINTENDENT);
    const assigned =
      currentRfi && !currentRfi.rfi_questions.find((qus) => !qus.assignee);
    const today = moment();
    let dueDateCountdown =
      form && form.dueDate
        ? countdown(today.format('YYYY-MM-DD'), form.dueDate)
        : null;
    let extendedDueDateCountdown =
      form && form.extendedDueDate
        ? countdown(today.format('YYYY-MM-DD'), form.extendedDueDate)
        : null;
    if (dueDateCountdown) {
      dueDateCountdown += today > moment(form.dueDate) ? ' past due' : ' left';
    }
    if (extendedDueDateCountdown) {
      extendedDueDateCountdown +=
        today > moment(form.extendedDueDate) ? ' past due' : ' left';
    }

    return (
      <div className="rfi-form">
        {(!currentRfi || form.csiCodeId) && (
          <Form horizontal>
            <FormGroup>
              <Col componentClass={ControlLabel} sm={1}>
                CSI Code
              </Col>
              <Col sm={4}>
                <Dropdown
                  className={classnames({
                    'form-error': form.errors.csiCodeId,
                  })}
                  placeholder="Select Code..."
                  value={form.csiCodeId}
                  onChange={(option) => this.handleCsiCodeChange(option.value)}
                  options={this.rfiCsiCodeOptions()}
                />
              </Col>
              <Col componentClass={ControlLabel} sm={1}>
                Status
              </Col>
              <Col sm={3}>
                {subcontractor ? (
                  <span className="status-locked">
                    {statusLabel.charAt(0).toUpperCase()}
                    {statusLabel.slice(1)}
                    <img className="locked-icon" src={lockPng} alt="locked" />
                  </span>
                ) : (
                  <Dropdown
                    className={classnames({ 'form-error': form.errors.status })}
                    placeholder="Select Status..."
                    value={form.status}
                    onChange={(option) =>
                      this.handleFormChange('status', option.value)
                    }
                    options={this.rfiStatusOptions()}
                  />
                )}
              </Col>
            </FormGroup>
            {!subcontractor && subOptions.length > 1 && (
              <FormGroup>
                <Col componentClass={ControlLabel} sm={1}>
                  Subcontractor
                </Col>
                <Col sm={4}>
                  <Dropdown
                    placeholder="Select Subcontractor..."
                    value={form.contractorId}
                    onChange={(option) =>
                      this.handleFormChange('contractorId', option.value)
                    }
                    options={subOptions}
                  />
                </Col>
              </FormGroup>
            )}
            {!subcontractor && (
              <FormGroup>
                <Col componentClass={ControlLabel} sm={1}>
                  CC CSI Code(s)
                </Col>
                <Col sm={6}>
                  <MultipleDropdown
                    options={this.rfiCcCsiCodeOptions()}
                    currentValues={form.relCsiCodes}
                    placeholder="Select CSI Codes to CC"
                    onChange={(values) =>
                      this.handleFormChange('relCsiCodes', values)
                    }
                  />
                </Col>
              </FormGroup>
            )}
            {!subcontractor && (
              <FormGroup>
                <Col componentClass={ControlLabel} sm={1}>
                  CC Recipient(s)
                </Col>
                <Col sm={6}>
                  <MultipleDropdown
                    options={this.recipientOptions()}
                    currentValues={form.recipients}
                    placeholder="Select Contributors to CC"
                    onChange={(values) =>
                      this.handleFormChange('recipients', values)
                    }
                  />
                </Col>
              </FormGroup>
            )}
            <FormGroup>
              <Col componentClass={ControlLabel} sm={1}>
                Due Date
              </Col>
              <Col sm={4}>
                <div
                  className={classnames({ 'form-error': form.errors.dueDate })}
                >
                  <DatetimePicker
                    isValidDate={todayValidation}
                    placeholder="Choose Date..."
                    value={form.dueDate}
                    onChange={(value) =>
                      this.handleFormChange('dueDate', value)
                    }
                  />
                </div>
              </Col>
              {dueDateCountdown && (
                <Col componentClass={ControlLabel} sm={4} className="countdown">
                  {dueDateCountdown}
                </Col>
              )}
            </FormGroup>
            {assigned && (
              <FormGroup>
                <Col componentClass={ControlLabel} sm={1}>
                  Extended Due Date
                </Col>
                <Col sm={4}>
                  <div
                    className={classnames({
                      'form-error': form.errors.extendedDueDate,
                    })}
                  >
                    <DatetimePicker
                      isValidDate={todayValidation}
                      placeholder="Choose Date..."
                      value={form.extendedDueDate}
                      onChange={(value) =>
                        this.handleFormChange('extendedDueDate', value)
                      }
                    />
                  </div>
                </Col>
                {extendedDueDateCountdown && (
                  <Col
                    componentClass={ControlLabel}
                    sm={4}
                    className="countdown"
                  >
                    {extendedDueDateCountdown}
                  </Col>
                )}
              </FormGroup>
            )}
            <FormGroup>
              <Col componentClass={ControlLabel} sm={1}>
                Title
              </Col>
              <Col sm={11}>
                <TextareaAutosize
                  className={classnames({ 'form-error': form.errors.title })}
                  rows={1}
                  placeholder="Enter Text Here..."
                  defaultValue={form.title}
                  onBlur={(e) => this.handleFormChange('title', e.target.value)}
                />
              </Col>
            </FormGroup>
          </Form>
        )}
      </div>
    );
  }
}

export default connect((state) => {
  return {
    projectProposal: getProjectProposal(state),
    directory: getDirectory(state),
    form: getRfiForm(state),
  };
})(RfiForm);



// WEBPACK FOOTER //
// ./src/components/admin/projects/details/project-rfi-log/RfiForm.js